/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { PutFrontRichiesteIdRiepilogoBody } from '../schemas/putFrontRichiesteIdRiepilogoBody.ts';
import type { WsProblema } from '../schemas/wsProblema.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Carica il documento PDF di riepilogo della richiesta firmato dall'utente
 * @summary API di Upload del Riepilogo firmato.
 */
export const putFrontRichiesteIdRiepilogo = (
  id: string,
  putFrontRichiesteIdRiepilogoBody: PutFrontRichiesteIdRiepilogoBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', putFrontRichiesteIdRiepilogoBody.file);

  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/richieste/${id}/riepilogo`,
      method: 'put',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPutFrontRichiesteIdRiepilogoMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdRiepilogo>>,
    TError,
    { id: string; data: PutFrontRichiesteIdRiepilogoBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontRichiesteIdRiepilogo>>,
  TError,
  { id: string; data: PutFrontRichiesteIdRiepilogoBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontRichiesteIdRiepilogo>>,
    { id: string; data: PutFrontRichiesteIdRiepilogoBody }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontRichiesteIdRiepilogo(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontRichiesteIdRiepilogoMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontRichiesteIdRiepilogo>>
>;
export type PutFrontRichiesteIdRiepilogoMutationBody =
  PutFrontRichiesteIdRiepilogoBody;
export type PutFrontRichiesteIdRiepilogoMutationError = ErrorType<WsProblema>;

/**
 * @summary API di Upload del Riepilogo firmato.
 */
export const usePutFrontRichiesteIdRiepilogo = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdRiepilogo>>,
    TError,
    { id: string; data: PutFrontRichiesteIdRiepilogoBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontRichiesteIdRiepilogoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Permette di scaricare il documento PDF firmato di riepilogo della richiesta precedentemente caricato dall'utente.
 * @summary API di Download del Riepilogo firmato.
 */
export const getFrontRichiesteIdRiepilogoDownload = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/richieste/${id}/riepilogo/download`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdRiepilogoDownloadQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/riepilogo/download`] as const;

export const getGetFrontRichiesteIdRiepilogoDownloadQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdRiepilogoDownloadQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>
  > = ({ signal }) =>
    getFrontRichiesteIdRiepilogoDownload(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdRiepilogoDownloadQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>
>;
export type GetFrontRichiesteIdRiepilogoDownloadQueryError =
  ErrorType<WsProblema>;

/**
 * @summary API di Download del Riepilogo firmato.
 */
export const useGetFrontRichiesteIdRiepilogoDownload = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdRiepilogoDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdRiepilogoDownloadQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
